import { useTranslation } from 'react-i18next';

import {
  ResourcePicker,
  type PickerQuery,
  type PickerSelection,
} from '@nucleus-commerce/admin-kit/resource-picker';
import type { Collection } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Badge } from '@nucleus-commerce/ui/components/badge';

import { resourcePickersApi } from '../api/resource-pickers-api';
import { resourcePickerKeys } from '../hooks/resource-picker-keys';

import { PICKER_PAGE_SIZE } from './product-filter-defs';

const BY_ID_CONCURRENCY = 6;

export interface CollectionPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storeId: string;
  title?: string;
  confirmLabel?: string;
  /** `false` (default) for one collection, `true` for any number, a number to cap. */
  multiple?: boolean | number;
  initialQuery?: string;
  initialSelectedIds?: readonly string[];
  lockedIds?: ReadonlySet<string>;
  /** What a locked row says instead of "Already added". */
  lockedLabel?: string;
  onConfirm: (selection: PickerSelection<Collection>) => unknown;
}

/**
 * A store's collections in the same shell: search only, "12 products" under each title, endless
 * paging. Single by default; with `multiple` a "Selected (n)" tab reviews the picks, loading the
 * initial ones that are not on a loaded page by id.
 */
export function CollectionPicker({
  open,
  onOpenChange,
  storeId,
  title,
  confirmLabel,
  multiple = false,
  initialQuery,
  initialSelectedIds,
  lockedIds,
  lockedLabel,
  onConfirm,
}: CollectionPickerProps) {
  const { t, i18n } = useTranslation(['resourcePickers', 'common']);
  const number = new Intl.NumberFormat(i18n.language);
  const getByIds = async (ids: readonly string[], signal: AbortSignal) => {
    const rows: Collection[] = [];
    // One read per id (the list has no id filter); the Selected tab asks for at most 100.
    for (let i = 0; i < ids.length; i += BY_ID_CONCURRENCY) {
      const batch = await Promise.all(
        ids.slice(i, i + BY_ID_CONCURRENCY).map((id) =>
          resourcePickersApi.collection(storeId, id, signal).catch((error: unknown) => {
            if (isApiError(error) && error.status === 404) return null;
            throw error;
          }),
        ),
      );
      rows.push(...batch.filter((row): row is Collection => row !== null));
    }
    return rows;
  };
  const list = (query: PickerQuery) => {
    const params = { limit: PICKER_PAGE_SIZE, ...(query.q ? { q: query.q } : {}) };
    return {
      queryKey: resourcePickerKeys.collections(storeId, params),
      queryFn: ({ cursor, signal }: { cursor: string | undefined; signal: AbortSignal }) =>
        resourcePickersApi.collections(
          storeId,
          { ...params, ...(cursor ? { cursor } : {}) },
          signal,
        ),
    };
  };
  return (
    <ResourcePicker<Collection>
      open={open}
      onOpenChange={onOpenChange}
      title={title ?? t(multiple === false ? 'collections.titleOne' : 'collections.title')}
      confirmLabel={confirmLabel ?? t('common:resourcePicker.add')}
      searchPlaceholder={t('collections.search')}
      emptyTitle={t('collections.empty')}
      countLabel={(count, max) =>
        max === undefined
          ? t('collections.selected', { count, number: number.format(count) })
          : t('collections.selectedOf', {
              count,
              number: number.format(count),
              max: number.format(max),
            })
      }
      multiple={multiple}
      {...(initialQuery !== undefined ? { initialQuery } : {})}
      {...(initialSelectedIds ? { initialSelectedIds } : {})}
      list={list}
      getId={(collection) => collection.id}
      {...(multiple === false ? {} : { selectedTab: true, getByIds })}
      getRow={(collection) => ({
        title: collection.title,
        description: [
          t('collections.productCount', { count: collection.productCount }),
          collection.kind === 'automated' ? t('collections.automated') : null,
        ]
          .filter(Boolean)
          .join(' · '),
        thumbnail: null,
        badge:
          collection.status === 'active' ? undefined : (
            <Badge variant="outline" className="text-muted-foreground font-normal">
              {t('collections.hidden')}
            </Badge>
          ),
        ...(lockedIds?.has(collection.id)
          ? { disabled: lockedLabel ?? t('alreadyAdded'), checked: true }
          : {}),
      })}
      onConfirm={onConfirm}
    />
  );
}
